import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { SearchModal, SearchResult } from "../common/searchModal";
import {
  Category,
  QualifiedFeature,
  QualifiedFeatureFragment,
} from "fe-common/graphql";
import { DataInputSectionConfiguration } from "../config/configurationTypes";
import { DataInputSectionConfigurations } from "../config/dataInputSectionConfiguration";
import {
  FeatureWithName,
  useFeatureSuggestions,
} from "fe-web-components/autocomplete";
import { usePatientCase } from "../usePatientCase";
import { EditablePanelItem } from "../editablePanelItem";
import {
  PanelContext,
  panelContext2String,
  PatientCaseGrouping,
  RefinePopupContextProps,
} from "../../../context/diagnosticAgent";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { KahunPanelSubGroup } from "shared";
import { RefinePopup } from "../refineFeature/refinePopup";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { RoundBottomNavigationButton } from "../common/rosebudButtomNavigationButton";
import { useScreenSize } from "../util/useScreenSize";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { startCase } from "lodash";
import { DataInput } from "../../dataInputTypes";
import { useSmartPanels } from "../useSmartPanels";
import { useRouteHelper } from "../useRouteHelper";
import { useChangePatientCase } from "./useChangePatientCase";
import { groupQualifiedWithLevel } from "fe-web-components/autocomplete/groupFeaturesWithLevel";
//region [[ Styles ]]

const SearchIconContainer = styled(RoundBottomNavigationButton)(
  ({ theme }) => ({
    borderRadius: "25px",
    background: "white",

    "&:hover": {
      background: "white",
      color: KahunColorMap.Default + " !important",
    },

    fontSize: "1.2em",
    paddingRight: theme.spacing(2),
    color: KahunColorMap.Default,
    borderColor: "white",
    paddingLeft: theme.spacing(2),
    boxShadow: "0px 2px 5px 0px rgb(60 64 67 / 16%)",
    "& i": {
      color: KahunColorMap.DefaultLight,
      marginRight: "8px",
    },

    [theme.breakpoints.down(MobileView)]: {
      width: "100%",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      fontSize: "14px",
      padding: theme.spacing(0.5),
    },
  }),
);

const SearchEditablePanelItem = styled(EditablePanelItem)(({ theme }) => ({
  margin: "-9px",
  [theme.breakpoints.down(MobileView)]: {
    marginLeft: "-40px",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]
type PanelResult = FeatureWithName;

export interface PanelsSearchProps {
  className?: string;
  searchContext: PanelContext;
  results?: any[];
}

//endregion [[ Props ]]

//region [[ Functions ]]
const getSearchCategoriesForPanelCategory = (context: PanelContext) => {
  const panelConfig = getConfigForCat(context.panelCategory);
  return (
    (panelConfig.searchCategories && panelConfig.searchCategories(context)) ||
    panelConfig.defaultForCategories
  );
};

const getConfigForCat = (cat: Category) =>
  (DataInputSectionConfigurations[cat?.id] ||
    {}) as DataInputSectionConfiguration;

//endregion [[ Functions ]]

export const PanelsSearch = ({
  searchContext,
  ...props
}: PanelsSearchProps) => {
  const { caseGrouping, panelCategory } = searchContext;

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { navigateTo } = useRouteHelper();

  const [term, setTerm] = useState<string | null>("");

  const [refinePopupProps, setRefinePopupPros] = useState<
    RefinePopupContextProps
  >(null);

  const { patientCase } = usePatientCase();

  const { addProcedurePanel } = useSmartPanels();
  const { handlePanelsToAdd } = usePatientCase();

  const { isMobile } = useScreenSize();

  const { setCaseValue, getCaseValue } = useChangePatientCase();

  const getResult = useCallback(
    (
      qf: QualifiedFeature,
      section: PatientCaseGrouping,
      subGroupId?: string,
    ) => {
      if (
        section === PatientCaseGrouping.WORKUP &&
        subGroupId === KahunPanelSubGroup.Procedures &&
        !props.results
      ) {
        if (
          qf.qualifiers?.length &&
          patientCase.panelsToAdd?.some(
            (p) => p.qualifiers[0].value.id === qf.qualifiers[0].value.id,
          )
        ) {
          return { negate: false };
        } else {
          return null;
        }
      } else {
        return getCaseValue(qf, section);
      }
    },
    [getCaseValue, patientCase.panelsToAdd, props.results],
  );

  const resultSelected = useCallback(
    async (
      qf: QualifiedFeatureFragment,
      response: DataInput,
      section: PatientCaseGrouping,
      panelContext: PanelContext,
    ) => {
      if (
        section === PatientCaseGrouping.WORKUP &&
        panelContext.panelCategory.id === KahunPanelSubGroup.Procedures &&
        !props.results
      ) {
        handlePanelsToAdd(qf);
        const newPanel = await addProcedurePanel(qf);

        navigateTo({
          grouping: PatientCaseGrouping.WORKUP,
          subGroupId: KahunPanelSubGroup.Procedures,
          panelId: newPanel.id,
        });
      } else {
        setCaseValue(qf, response, section, {
          panel: panelContext.panel,
          panelCategory: panelContext.panelCategory,
          caseGrouping: section,
          fromSearch: true,
        });
      }
    },
    [
      setCaseValue,
      addProcedurePanel,
      handlePanelsToAdd,
      props.results,
      navigateTo,
    ],
  );

  const searchFilter = useMemo(
    () => getSearchCategoriesForPanelCategory(searchContext),
    [searchContext],
  );

  const { loading, results } = useFeatureSuggestions(
    term,
    searchFilter,
    patientCase?.gender,
  );

  const handleSearchTermChanged = useCallback(
    (input: string) => {
      setTerm(input);
    },
    [setTerm],
  );

  // If we search within the panel itself display the full context in search
  // otherwise just display the category in which we search
  const searchContextString = useMemo(() => {
    const panelName = (searchContext?.panel?.title || "").toLowerCase();
    const panelCategoryName = searchContext?.panelCategory?.name?.toLowerCase();
    const showPanelName = getConfigForCat(searchContext.panelCategory)
      .searchSubCategories;
    if (showPanelName) return startCase(panelName);
    return startCase(
      panelCategoryName === "habits" ? "risk factors" : panelCategoryName,
    );
  }, [searchContext]);

  // For logging
  const panelContextString = useMemo(() => panelContext2String(searchContext), [
    searchContext,
  ]);

  const handlePanelItemSelected = useCallback(
    (i) => {
      evt({
        category: CATEGORY.PANELS,
        action: ACTION.PANEL_SEARCH_ITEM_SELECTED,
        label: `${i.name} - term used: ${term},  context:${panelContextString}`,
      });
    },
    [term, panelContextString],
  );

  const handleRefineFinding = useCallback(
    (feature: QualifiedFeature) => {
      evt({
        category: CATEGORY.PANELS,
        action: ACTION.PANEL_SEARCH_REFINE,
        label: `${feature.name} - term used: ${term}, context:${panelContextString}`,
      });

      setRefinePopupPros({
        open: true,
        refineQualifier: feature,
        patientCaseGrouping: caseGrouping,
      });
    },
    [setRefinePopupPros, caseGrouping, term, panelContextString],
  );

  const renderSearchResult = useCallback(
    (itemToRender: SearchResult<PanelResult>) => {
      const { value: feature } = itemToRender.value;
      return (
        <SearchEditablePanelItem
          key={feature?.id}
          qualifiedFeature={feature}
          isThreeway
          onRefine={handleRefineFinding}
          getCaseValue={getResult}
          setCaseValue={resultSelected}
          panelContext={{ ...searchContext, fromSearch: true }}
        />
      );
    },
    [handleRefineFinding, searchContext, resultSelected, getResult],
  );

  const handleRefineIsDone = useCallback(() => {
    setRefinePopupPros({ open: false });
  }, [setRefinePopupPros]);

  /***
   * Some panel sub groups add a relation qualifier to the feature (such as family
   * history of X vs just having X) The relation is configured under the section configurations
   * (@see dataInputSectionConfiguration.ts) so we pull the configuration object and run `transformResult`
   * */
  const linkRelationToResults = useCallback(
    (suggestions: PanelResult[]) => {
      const config = getConfigForCat(
        panelCategory,
      ) as DataInputSectionConfiguration;

      return config.transformResult
        ? suggestions.map((featureWithName) => ({
            ...featureWithName,
            value: config.transformResult(featureWithName.value, searchContext),
          }))
        : suggestions;
    },
    [panelCategory, searchContext],
  );

  const isTermEmpty = useMemo(() => !term || term.trim() === "", [term]);

  const searchResults = useMemo(() => {
    if (props.results) {
      return isTermEmpty
        ? null
        : props.results
            .filter((result) =>
              result.qualifiers[0].value.name
                .toLowerCase()
                .startsWith(term.toLowerCase()),
            )
            .map((result) => {
              return {
                name: result.name,
                value: result,
              };
            });
    }

    return isTermEmpty
      ? null
      : groupQualifiedWithLevel(linkRelationToResults(results));
  }, [isTermEmpty, linkRelationToResults, results, props.results, term]);

  const reportSearchOpened = useCallback(
    (e) => {
      evt({
        category: CATEGORY.PANELS,
        action: ACTION.PANEL_SEARCH_OPEN,
        label: `context:${panelContextString}`,
      });
      setAnchorEl(e.currentTarget);
    },
    [panelContextString],
  );

  const openButton = useMemo(
    () => (
      <SearchIconContainer variant={"outlined"} onClick={reportSearchOpened}>
        <i className="far fa-search" />
        Search {isMobile ? null : searchContextString}
      </SearchIconContainer>
    ),
    [reportSearchOpened, isMobile, searchContextString],
  );

  const handleSearchClosed = useCallback(() => {
    evt({
      category: CATEGORY.PANELS,
      action: ACTION.PANEL_SEARCH_CLOSE,
      label: `term: ${term}, context:${panelContextString}`,
    });

    // Clear term
    setTerm("");
  }, [term, panelContextString, setTerm]);

  const placeholderTextFromContext = useMemo(
    () => `Search ${searchContextString}`,
    [searchContextString],
  );

  return (
    <SearchModal
      className={props.className}
      onItemSelected={handlePanelItemSelected}
      onValueChange={handleSearchTermChanged}
      renderResult={renderSearchResult}
      openSearchButton={openButton}
      results={searchResults}
      loading={loading}
      onDone={handleSearchClosed}
      placeholder={placeholderTextFromContext}
      anchorEl={anchorEl}
      multiple
    >
      <RefinePopup
        context={refinePopupProps}
        onDone={handleRefineIsDone}
        showDelete={false}
      />
    </SearchModal>
  );
};
